import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';
import { useAppDispatch } from '../../store';
import apis from '../../utils/apis';
import { formatRoleNameForDisplay } from '../../utils/roleFormatter';
import DetailFrame from '../shared/Frames/DetailFrame';
import Button from '../shared/TailwindComponents/Button';
import Badge from '../shared/TailwindComponents/Badge';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { showModal } from '../../store/actions/modalActions';
import { PiGearLight } from 'react-icons/pi';
import * as PiIcons from 'react-icons/pi';

// Icon mapping for permission sections
const sectionIconMap: Record<string, string> = {
  'Dashboard': 'PiDashboard',
  'Tasks': 'PiCheckSquare',
  'Calendar': 'PiCalendar',
  'Messages': 'PiChat',
  'Clients': 'PiUsers',
  'Lawyers': 'PiUserCircle',
  'Projects': 'PiTreeStructure',
  'Matters': 'PiBriefcase',
  'Files': 'PiFiles',
  'Research': 'PiMagnifyingGlass',
  'Tables': 'PiGridFour',
  'Analytics': 'PiChartBar',
  'Data Sources': 'PiDatabase',
  'Finder': 'PiUserSwitch',
  'Templates': 'PiFileText',
  'People': 'PiUsers',
  'Departments': 'PiBuilding',
  'Teams': 'PiUsersThree',
  'Finance': 'PiCoinVertical',
};

const getIconByName = (name: string): JSX.Element => {
  const normalizedName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  const spacedName = name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  const iconKey = sectionIconMap[normalizedName] || sectionIconMap[spacedName] || sectionIconMap[name] || 'PiGear';
  const IconComponent = (PiIcons as any)[iconKey + 'Light'] || (PiIcons as any)[iconKey];
  return IconComponent ? <IconComponent className="h-4 w-4" /> : <PiGearLight className="h-4 w-4" />;
};

// Permission display constants
const HIDDEN_PERMISSION_SECTIONS = ['dashboard', 'research', 'client_pipeline', 'analytics', 'messages', 'finance', 'review', 'notifications', 'finder'];

const SECTION_DISPLAY_ORDER = [
  'tasks', 'calendar',
  'clients', 'matters', 'files',
  'tables', 'templates', 'data_sources', 
  'people', 'departments', 'teams',
];

const CATEGORY_ORDER = ['core', 'workspace', 'utilities', 'organization'];

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core',
  workspace: 'Workspace',
  utilities: 'Utilities',
  organization: 'Organization',
};

const formatSectionName = (name: string): string => {
  return name.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const formatPermissionLabel = (permission: { name: string; description?: string }): string => {
  if (permission.description) return permission.description;
  const parts = permission.name.split(':');
  const action = parts[1] || parts[0];
  return action.charAt(0).toUpperCase() + action.slice(1);
};

interface Permission {
  id: number;
  name: string;
  section_id: number;
  description?: string;
}

interface PermissionSection {
  id: number;
  name: string;
  category?: string;
  description?: string;
  permissions: Permission[];
}

interface Role {
  id: string | number;
  name: string;
  description?: string;
  permission_ids: number[];
}

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
}

const AdminRoleDetail: React.FC = () => {
  const { t } = useTranslation();
  const { roleId } = useParams<{ roleId: string }>();
  const history = useHistory();
  const dispatch = useAppDispatch();

  const [role, setRole] = useState<Role | null>(null);
  const [permissions, setPermissions] = useState<PermissionSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const hasInitialized = useRef(false);

  // Fetch data only once on component mount
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!roleId) {
          throw new Error('No role ID provided in URL');
        }

        let roleRes;

        // Try custom role endpoint first
        try {
          roleRes = await apis.get(`/admin/roles/${roleId}`);
        } catch (customErr: any) {
          // If custom role not found or network error, try static role endpoint
          try {
            roleRes = await apis.get(`/role-permissions/${roleId}`);
          } catch (staticErr: any) {
            console.error('[AdminRoleDetail] Static role fetch also failed');
            console.error('[AdminRoleDetail] Static error status:', staticErr?.response?.status);
            console.error('[AdminRoleDetail] Static error message:', staticErr?.message);
            throw customErr; // Throw original error if both fail
          }
        }

        // Fetch permissions
        const permsRes = await apis.get('/admin/permissions');

        const roleData = roleRes.data?.data;
        if (!roleData) {
          throw new Error('Role data is empty in response');
        }

        setRole(roleData);
        setPermissions(permsRes.data?.data || []);
      } catch (err: any) {

        // Extract meaningful error message
        let errorMsg = 'Unknown error';
        if (err?.response?.data?.error) {
          errorMsg = err.response.data.error;
        } else if (err?.message) {
          errorMsg = err.message;
        }

        setError(errorMsg);
        dispatch(showFeedbackModal({
          showModal: true,
          message: `Failed to load role: ${errorMsg}`,
          modalType: 'error',
          duration: 5000
        }));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [roleId, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAssignRole = async () => {
    if (!role) {
      dispatch(showFeedbackModal({
        showModal: true,
        message: t('Role not loaded'),
        modalType: 'error',
        duration: 3000
      }));
      return;
    }

    dispatch(showModal({
      showModal: true,
      type: 'adminAssignRole',
      roleId: role.id,
      message: t('Assign Role'),
      onConfirm: async (selectedUserId: string) => {
        if (!selectedUserId) {
          dispatch(showFeedbackModal({
            showModal: true,
            message: t('Please select a user'),
            modalType: 'warning',
            duration: 3000
          }));
          return;
        }

        try {
          const res = await apis.post(`/admin/users/${selectedUserId}/roles`, {
            role_name: role.name,
          });

          if (res.data?.success) {
            dispatch(showFeedbackModal({
              showModal: true,
              message: t('Role assigned successfully'),
              modalType: 'success',
              duration: 3000
            }));
          }
        } catch (err: any) {
          dispatch(showFeedbackModal({
            showModal: true,
            message: err?.response?.data?.error || t('Failed to assign role'),
            modalType: 'error',
            duration: 4000
          }));
        }
      }
    }));
  };

  // Loading state
  if (loading) {
    const breadcrumbs = [
      { name: t('Admin'), href: '/admin', current: false },
      { name: t('Roles'), href: '/admin?tab=roles', current: false },
      { name: t('Loading...'), href: '#', current: true },
    ];

    return (
      <DetailFrame breadcrumbs={breadcrumbs}>
        <div className="flex-1 overflow-y-auto px-4 py-6">
          {/* Title and description Skeleton */}
          <div className="mb-8">
            <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded w-1/3 mb-3 animate-fadePulse" />
            <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 animate-fadePulse" />
          </div>

          {/* Permissions Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm animate-fadePulse"
              >
                {/* Header skeleton */}
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex-shrink-0 w-8 h-8 bg-gray-300 dark:bg-gray-600 rounded animate-fadePulse" />
                  <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded w-1/2 flex-1 animate-fadePulse" />
                </div>

                {/* Permissions list skeleton */}
                <div className="space-y-2">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-5 h-5 bg-gray-300 dark:bg-gray-600 rounded animate-fadePulse" />
                      <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-2/3 flex-1 animate-fadePulse" />
                    </div>
                  ))}
                </div>

                {/* Count skeleton */}
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="h-3 bg-gray-300 dark:bg-gray-600 rounded w-1/3 animate-fadePulse" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </DetailFrame>
    );
  }

  // Error state
  if (error || !role) {
    const breadcrumbs = [
      { name: t('Admin'), href: '/admin', current: false },
      { name: t('Roles'), href: '/admin?tab=roles', current: false },
      { name: t('Role Not Found'), href: '#', current: true },
    ];

    return (
      <DetailFrame breadcrumbs={breadcrumbs}>
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <p className="text-red-600 dark:text-red-400 mb-4">{error || t('Role not found')}</p>
            <Button
              variant="secondary"
              onClick={() => history.push('/admin?tab=roles')}
            >
              {t('Back to Roles')}
            </Button>
          </div>
        </div>
      </DetailFrame>
    );
  }

  // Build breadcrumbs with actual role name
  const breadcrumbs = [
    { name: t('Admin'), href: '/admin', current: false },
    { name: t('Roles'), href: '/admin?tab=roles', current: false },
    { name: formatRoleNameForDisplay(role.name), href: '#', current: true },
  ];

  // Build a set of permission IDs to show
  let permissionIdsToShow = new Set<number>();
  const rawPermissionNames = (role as any).permission_names || [];

  if (role.permission_ids && role.permission_ids.length > 0) {
    // Use matched IDs if available
    permissionIdsToShow = new Set(role.permission_ids);
  } else if (rawPermissionNames && rawPermissionNames.length > 0) {
    // Fall back to matching raw names against fetched permissions
    permissions.forEach(section => {
      section.permissions.forEach(perm => {
        // Try exact name match
        if (rawPermissionNames.includes(perm.name)) {
          permissionIdsToShow.add(perm.id);
        }
        // Try fuzzy match: "dashboard:read" might match "Dashboard" or "Read"
        else {
          const rawNameLower = rawPermissionNames.join('|').toLowerCase();
          const permNameLower = perm.name.toLowerCase();
          if (rawNameLower.includes(permNameLower.split(/\s+/)[0])) {
            // Partial match on first word
            permissionIdsToShow.add(perm.id);
          }
        }
      });
    });
  }

  // Filter permissions to only show those assigned to this role
  const rolePermissions = permissions
    .filter(s => !HIDDEN_PERMISSION_SECTIONS.includes(s.name.toLowerCase()))
    .map(section => ({
      ...section,
      permissions: section.permissions.filter(p => permissionIdsToShow.has(p.id))
    }))
    .filter(section => section.permissions.length > 0)
    .sort((a, b) => {
      const idxA = SECTION_DISPLAY_ORDER.indexOf(a.name.toLowerCase());
      const idxB = SECTION_DISPLAY_ORDER.indexOf(b.name.toLowerCase());
      return (idxA === -1 ? 999 : idxA) - (idxB === -1 ? 999 : idxB);
    });

  const groupedRolePermissions = CATEGORY_ORDER
    .map(cat => ({
      category: cat,
      label: CATEGORY_LABELS[cat],
      sections: rolePermissions.filter(s => (s.category || 'core') === cat),
    }))
    .filter(g => g.sections.length > 0);

  const headerActions = [
    {
      label: t('Assign Role'),
      onClick: handleAssignRole,
      variant: 'primary' as const,
    }
  ];

  return (
    <DetailFrame breadcrumbs={breadcrumbs} actions={headerActions}>
      <div className="flex-1 overflow-y-auto px-4 py-6">
        {/* Title and description */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {formatRoleNameForDisplay(role.name)}
          </h1>
          {role.description && (
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {role.description}
            </p>
          )}
        </div>

        {/* Permissions Grid */}
        {groupedRolePermissions.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">{t('No permissions assigned')}</p>
          </div>
        ) : (
          <div className="space-y-8">
            {groupedRolePermissions.map(group => (
              <div key={group.category}>
                <h2 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4">
                  {group.label}
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {group.sections.map(section => (
              <div
                key={section.id}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-sm hover:shadow-md transition-shadow"
              >
                {/* Header with icon and title */}
                <div className="flex items-center gap-3 mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center text-gray-600 dark:text-gray-400">
                    {getIconByName(section.name)}
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                      {formatSectionName(section.name)}
                    </h3>
                    {section.description && (
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {section.description}
                      </p>
                    )}
                  </div>
                </div>

                {/* Permissions list with checkmarks */}
                <div className="space-y-2">
                  {section.permissions.map(perm => (
                    <div key={perm.id} className="flex items-start gap-2">
                      <span className="flex-shrink-0 w-5 h-5 text-green-600 dark:text-green-500 mt-0.5">
                      <PiIcons.PiCheckCircleLight className='text-green-600'/>
                      </span>
                      <span className="text-xs text-gray-700 dark:text-gray-300">
                        {formatPermissionLabel(perm)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Permission count */}
                <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {section.permissions.length} {section.permissions.length === 1 ? t('permission') : t('permissions')}
                  </p>
                </div>
              </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

    </DetailFrame>
  );
};

export default AdminRoleDetail;
